import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardActivityFormPayload,
	ACTIVITY_CSV_LABELS,
	ActivityCsvKeys,
	ActivityCsvTemplate,
	ACTIVITY_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { LanguageDropdownField } from '../../../fields/setup-wizard/language-dropdown-field/language-dropdown-field';
import { TextareaField } from '../../../fields/setup-wizard/textarea-field/textarea-field';
import { FormUtil } from '../../../../utils/form.util';

type ActivityForm = {
	[ActivityCsvKeys.ssoActivityId]: FormControl<string | null>;
	[ActivityCsvKeys.ssoLessonId]: FormControl<string | null>;
	[ActivityCsvKeys.defaultLangId]: FormControl<string | null>;
	[ActivityCsvKeys.displayOrder]: FormControl<number | null>;
	[ActivityCsvKeys.isInstruction]: FormControl<boolean | null>;
	[ActivityCsvKeys.enName]: FormControl<string | null>;
	[ActivityCsvKeys.enImageUri]: FormControl<string | null>;
	[ActivityCsvKeys.enDescriptionShort]: FormControl<string | null>;
	[ActivityCsvKeys.enDescriptionHtml]: FormControl<string | null>;
	[ActivityCsvKeys.esName]: FormControl<string | null>;
	[ActivityCsvKeys.esImageUri]: FormControl<string | null>;
	[ActivityCsvKeys.esDescriptionShort]: FormControl<string | null>;
	[ActivityCsvKeys.esDescriptionHtml]: FormControl<string | null>;
	[ActivityCsvKeys.activityUuid]: FormControl<string | null>;
	[ActivityCsvKeys.lessonUuid]: FormControl<string | null>;
	[ActivityCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-activity-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		IntegerField,
		LanguageDropdownField,
		TextareaField,
	],
	standalone: true,
	templateUrl: './setup-wizard-activity-form.html',
	styleUrl: './setup-wizard-activity-form.scss',
})
export class SetupWizardActivityForm {
	public lessonUuid = input.required<string>();
	public edit = input<ActivityCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardActivityFormPayload>();
	public ActivityCsvKeys = ActivityCsvKeys;
	public ACTIVITY_CSV_LABELS = ACTIVITY_CSV_LABELS;
	public Validators = Validators;

	public form = new FormGroup<ActivityForm>({
		[ActivityCsvKeys.ssoActivityId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.ssoLessonId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.defaultLangId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.isInstruction]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.enName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.enImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.enDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.enDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.esName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.esImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.esDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.esDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.activityUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.lessonUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ActivityCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor() {
		FormUtil.applyCsvSpecValidators(this.form, ACTIVITY_CSV_SPEC);
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a activityUuid we can remove all validators for ssoActivityId
			if (edit[ActivityCsvKeys.activityUuid]) {
				this.form.get(ActivityCsvKeys.ssoActivityId)?.clearValidators();
				this.form.get(ActivityCsvKeys.ssoActivityId)?.updateValueAndValidity();
			}

			// If edit contains a lessonUuid we can remove all validators for ssoLessonId
			if (edit[ActivityCsvKeys.lessonUuid]) {
				this.form.get(ActivityCsvKeys.ssoLessonId)?.clearValidators();
				this.form.get(ActivityCsvKeys.ssoLessonId)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[ActivityCsvKeys.ssoActivityId]: edit[ActivityCsvKeys.ssoActivityId],
				[ActivityCsvKeys.ssoLessonId]: edit[ActivityCsvKeys.ssoLessonId],
				[ActivityCsvKeys.defaultLangId]: edit[ActivityCsvKeys.defaultLangId],
				[ActivityCsvKeys.displayOrder]: edit[ActivityCsvKeys.displayOrder],
				[ActivityCsvKeys.isInstruction]: edit[ActivityCsvKeys.isInstruction],
				[ActivityCsvKeys.enName]: edit[ActivityCsvKeys.enName],
				[ActivityCsvKeys.enImageUri]: edit[ActivityCsvKeys.enImageUri],
				[ActivityCsvKeys.enDescriptionShort]: edit[ActivityCsvKeys.enDescriptionShort],
				[ActivityCsvKeys.enDescriptionHtml]: edit[ActivityCsvKeys.enDescriptionHtml],
				[ActivityCsvKeys.esName]: edit[ActivityCsvKeys.esName],
				[ActivityCsvKeys.esImageUri]: edit[ActivityCsvKeys.esImageUri],
				[ActivityCsvKeys.esDescriptionShort]: edit[ActivityCsvKeys.esDescriptionShort],
				[ActivityCsvKeys.esDescriptionHtml]: edit[ActivityCsvKeys.esDescriptionHtml],
				[ActivityCsvKeys.activityUuid]: edit[ActivityCsvKeys.activityUuid],
				[ActivityCsvKeys.lessonUuid]: edit[ActivityCsvKeys.lessonUuid],
				[ActivityCsvKeys.active]: edit[ActivityCsvKeys.active],
			});
		} else {
			const lessonUuid = this.lessonUuid();

			if (!lessonUuid) {
				throw new Error('Lesson UUID is required');
			}

			this.form.patchValue({
				[ActivityCsvKeys.lessonUuid]: this.lessonUuid(),
			});
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[ActivityCsvKeys.ssoActivityId]: value[ActivityCsvKeys.ssoActivityId] ?? undefined,
				[ActivityCsvKeys.ssoLessonId]: value[ActivityCsvKeys.ssoLessonId] ?? undefined,
				[ActivityCsvKeys.defaultLangId]: value[ActivityCsvKeys.defaultLangId] ?? undefined,
				[ActivityCsvKeys.displayOrder]: value[ActivityCsvKeys.displayOrder] ?? undefined,
				[ActivityCsvKeys.isInstruction]: value[ActivityCsvKeys.isInstruction] ?? undefined,
				[ActivityCsvKeys.enName]: value[ActivityCsvKeys.enName] ?? undefined,
				[ActivityCsvKeys.enImageUri]: value[ActivityCsvKeys.enImageUri] ?? undefined,
				[ActivityCsvKeys.enDescriptionShort]: value[ActivityCsvKeys.enDescriptionShort] ?? undefined,
				[ActivityCsvKeys.enDescriptionHtml]: value[ActivityCsvKeys.enDescriptionHtml] ?? undefined,
				[ActivityCsvKeys.esName]: value[ActivityCsvKeys.esName] ?? undefined,
				[ActivityCsvKeys.esImageUri]: value[ActivityCsvKeys.esImageUri] ?? undefined,
				[ActivityCsvKeys.esDescriptionShort]: value[ActivityCsvKeys.esDescriptionShort] ?? undefined,
				[ActivityCsvKeys.esDescriptionHtml]: value[ActivityCsvKeys.esDescriptionHtml] ?? undefined,
				[ActivityCsvKeys.activityUuid]: value[ActivityCsvKeys.activityUuid] ?? undefined,
				[ActivityCsvKeys.lessonUuid]: value[ActivityCsvKeys.lessonUuid] ?? undefined,
				[ActivityCsvKeys.active]: value[ActivityCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}
}
